<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Admit Patient</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('ipd-dashboard')">IPD</a></li>
                      <li class="breadcrumb-item active">Admit Patient</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Left: Admission Form -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-header text-white">
                    <h5 class="mb-0"><i class="mdi mdi-bed me-2"></i>New Admission</h5>
                  </div>
                  <div class="card-body">
                    <!-- Step 1: Patient Selection -->
                    <div class="mb-4">
                      <h6 class="text-primary mb-3">
                        <span class="badge bg-primary rounded-circle me-2">1</span>
                        Patient Information
                      </h6>

                      <div v-if="!selectedPatient">
                        <div class="row">
                          <div class="col-md-8">
                            <label class="form-label">Search Patient</label>
                            <div class="input-group">
                              <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                              <input type="text" class="form-control" v-model="patientSearch"
                                     @input="searchPatients" placeholder="Search by name, ID, phone...">
                            </div>
                            <!-- Search Results -->
                            <div v-if="patientResults.length > 0" class="list-group mt-2 search-results-dropdown">
                              <a v-for="patient in patientResults" :key="patient.patientID" href="javascript:void(0);"
                                 class="list-group-item list-group-item-action" @click="selectPatient(patient)">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{{ patient.fullNames }}</strong>
                                    <small class="text-muted d-block">{{ patient.patientID }} | {{ patient.gender }}, {{ patient.age || calculateAge(patient.dateOfBirth) }} yrs</small>
                                  </div>
                                  <small class="text-muted">{{ patient.phoneNumber }}</small>
                                </div>
                              </a>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <label class="form-label">Or Select from Queue</label>
                            <select class="form-select" v-model="selectedVisitID" @change="selectFromQueue">
                              <option value="">-- Select Visit --</option>
                              <option v-for="visit in pendingVisits" :key="visit.visitID" :value="visit.visitID">
                                {{ visit.patientName }} - {{ visit.visitNo }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Selected Patient Display -->
                      <div v-else class="alert alert-success">
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center">
                            <div class="avatar-sm me-3">
                                <span class="avatar-title rounded-circle" :class="selectedPatient.gender === 'Male' ? 'bg-primary' : 'bg-danger'">
                                  {{ getInitials(selectedPatient.fullName || selectedPatient.patientName) }}
                                </span>
                            </div>
                            <div>
                              <h6 class="mb-0">{{ selectedPatient.fullName || selectedPatient.patientName }}</h6>
                              <small>{{ selectedPatient.patientNo || selectedPatient.patientID }} | {{ selectedPatient.gender }}, {{ selectedPatient.age || calculateAge(selectedPatient.dateOfBirth) }} yrs | {{ selectedPatient.phone || selectedPatient.phoneNumber }}</small>
                            </div>
                          </div>
                          <button type="button" class="btn btn-sm btn-outline-danger" @click="clearPatient">
                            <i class="mdi mdi-close"></i> Change
                          </button>
                        </div>
                      </div>
                    </div>

                    <hr>

                    <!-- Step 2: Ward & Bed Selection -->
                    <div class="mb-4">
                      <h6 class="text-primary mb-3">
                        <span class="badge bg-primary rounded-circle me-2">2</span>
                        Ward & Bed Assignment
                      </h6>

                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Select Ward <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="admissionForm.wardID" @change="loadBeds">
                            <option value="">-- Select Ward --</option>
                            <option v-for="ward in wards" :key="ward.wardID" :value="ward.wardID" :disabled="Number(ward.availableBeds) === 0">
                              {{ ward.wardName }} ({{ ward.wardType }}) - {{ ward.availableBeds }} beds available - KES {{ ward.chargePerDay || ward.dailyRate }}/day
                            </option>
                          </select>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Select Bed <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="admissionForm.bedID" :disabled="!admissionForm.wardID || beds.length === 0">
                            <option value="">-- Select Bed --</option>
                            <option v-for="bed in beds" :key="bed.bedID" :value="bed.bedID">
                              Bed {{ bed.bedNumber }} ({{ bed.bedType }})
                            </option>
                          </select>
                          <small v-if="admissionForm.wardID && beds.length === 0" class="text-danger">No available beds in this ward</small>
                        </div>
                      </div>

                      <!-- Bed Visual -->
                      <div v-if="admissionForm.wardID" class="mb-3">
                        <label class="form-label small text-muted">Bed Layout</label>
                        <div class="bed-layout p-3 bg-light rounded">
                          <div class="d-flex flex-wrap gap-2">
                            <div v-for="bed in allBeds" :key="bed.bedID"
                                 class="bed-item p-2 rounded text-center"
                                 :class="getBedClass(bed)"
                                 @click="selectBed(bed)"
                                 :title="getBedTitle(bed)">
                              <i class="mdi mdi-bed-outline d-block font-size-20"></i>
                              <small>{{ bed.bedNumber }}</small>
                            </div>
                          </div>
                          <div class="mt-2">
                            <small class="me-3"><span class="badge bg-success">&nbsp;</span> Available</small>
                            <small class="me-3"><span class="badge bg-danger">&nbsp;</span> Occupied</small>
                            <small><span class="badge bg-warning">&nbsp;</span> Selected</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr>

                    <!-- Step 3: Clinical Details -->
                    <div class="mb-4">
                      <h6 class="text-primary mb-3">
                        <span class="badge bg-primary rounded-circle me-2">3</span>
                        Clinical Details
                      </h6>

                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Admitting Doctor <span class="text-danger">*</span></label>
                          <select class="form-select" v-model="admissionForm.admittingDoctorID" @change="setDoctorName">
                            <option value="">-- Select Doctor --</option>
                            <option v-for="doc in doctors" :key="doc.staffID" :value="doc.staffID">
                              Dr. {{ doc.staffName }} - {{ doc.specialization || 'General' }}
                            </option>
                          </select>
                        </div>
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Admission Type</label>
                          <select class="form-select" v-model="admissionForm.admissionType">
                            <option value="ELECTIVE">Elective</option>
                            <option value="EMERGENCY">Emergency</option>
                            <option value="REFERRAL">Referral</option>
                            <option value="TRANSFER">Transfer</option>
                          </select>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-md-12 mb-3">
                          <label class="form-label">Reason for Admission <span class="text-danger">*</span></label>
                          <textarea class="form-control" rows="2" v-model="admissionForm.admissionReason" placeholder="Enter reason for admission..."></textarea>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-md-12 mb-3">
                          <label class="form-label">Provisional Diagnosis</label>
                          <textarea class="form-control" rows="2" v-model="admissionForm.diagnosis" placeholder="Enter provisional diagnosis..."></textarea>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Expected Length of Stay (days)</label>
                          <input type="number" class="form-control" v-model="admissionForm.expectedLOS" min="1" placeholder="e.g., 3">
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-footer bg-light">
                    <div class="d-flex justify-content-between">
                      <button type="button" class="btn btn-light" @click="goTo('ipd-dashboard')">
                        <i class="mdi mdi-arrow-left me-1"></i> Cancel
                      </button>
                      <button type="button" class="btn btn-primary btn-lg" @click="admitPatient" :disabled="!canAdmit || admitting">
                        <span v-if="admitting"><i class="mdi mdi-loading mdi-spin me-1"></i> Processing...</span>
                        <span v-else><i class="mdi mdi-check me-1"></i> Admit Patient</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Summary -->
              <div class="col-lg-4">
                <!-- Admission Summary -->
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="mdi mdi-clipboard-text me-2"></i>Admission Summary</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-sm table-borderless">
                      <tr>
                        <td class="text-muted">Patient:</td>
                        <td class="fw-medium">{{ selectedPatient?.fullName || selectedPatient?.patientName || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Ward:</td>
                        <td>{{ getWardName(admissionForm.wardID) || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Bed:</td>
                        <td>{{ getBedNumber(admissionForm.bedID) || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Doctor:</td>
                        <td>{{ admissionForm.admittingDoctorName || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Type:</td>
                        <td>{{ admissionForm.admissionType }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Daily Rate:</td>
                        <td class="fw-medium text-success">KES {{ numberFormat(getWardRate(admissionForm.wardID)) }}</td>
                      </tr>
                    </table>
                  </div>
                </div>

                <!-- Ward Info Card -->
                <div class="card" v-if="selectedWard">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><i class="mdi mdi-hospital-building me-2"></i>{{ selectedWard.wardName }}</h6>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Type:</span>
                      <span class="badge" :class="getWardTypeBadge(selectedWard.wardType)">{{ selectedWard.wardType }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Total Beds:</span>
                      <span>{{ selectedWard.actualBeds || selectedWard.totalBeds }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Available:</span>
                      <span class="text-success fw-medium">{{ selectedWard.availableBeds }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Occupied:</span>
                      <span class="text-warning fw-medium">{{ selectedWard.occupiedBeds }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Daily Rate:</span>
                      <span class="text-success fw-bold">KES {{ numberFormat(selectedWard.chargePerDay || selectedWard.dailyRate) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Quick Actions -->
                <div class="card">
                  <div class="card-body">
                    <h6 class="mb-3">Quick Links</h6>
                    <div class="d-grid gap-2">
                      <button class="btn btn-outline-primary" @click="goTo('ipd-dashboard')">
                        <i class="mdi mdi-view-dashboard me-1"></i> IPD Dashboard
                      </button>
                      <button class="btn btn-outline-secondary" @click="goTo('ward-management')">
                        <i class="mdi mdi-hospital-building me-1"></i> Ward Management
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Success Modal -->
    <div class="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center py-5">
            <div class="mb-4">
              <i class="mdi mdi-check-circle text-success" style="font-size: 80px;"></i>
            </div>
            <h4 class="mb-2">Patient Admitted Successfully!</h4>
            <p class="text-muted mb-1">Admission No: <strong>{{ admissionResult.admissionNo }}</strong></p>
            <p class="text-muted mb-4">
              {{ selectedPatient?.fullName || selectedPatient?.patientName }} has been admitted to
              {{ getWardName(admissionForm.wardID) }} - Bed {{ getBedNumber(admissionForm.bedID) }}
            </p>

            <div class="d-flex justify-content-center gap-2">
              <button class="btn btn-outline-primary" @click="newAdmission">
                <i class="mdi mdi-plus me-1"></i> New Admission
              </button>
              <button class="btn btn-primary" @click="goTo('ipd-dashboard')">
                <i class="mdi mdi-view-dashboard me-1"></i> IPD Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "AdmitPatient",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      // Patient Search
      patientSearch: '',
      patientResults: [],
      searchTimeout: null,
      selectedPatient: null,
      selectedVisit: null,
      selectedVisitID: '',
      pendingVisits: [],

      // Wards & Beds
      wards: [],
      beds: [],
      allBeds: [],
      selectedWard: null,

      // Doctors
      doctors: [],

      // Admission Form
      admissionForm: {
        wardID: '',
        bedID: '',
        admittingDoctorID: '',
        admittingDoctorName: '',
        admissionType: 'ELECTIVE',
        admissionReason: '',
        diagnosis: '',
        expectedLOS: ''
      },

      // State
      admitting: false,
      admissionResult: {},

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    canAdmit() {
      return this.selectedPatient &&
          this.admissionForm.wardID &&
          this.admissionForm.bedID &&
          this.admissionForm.admittingDoctorID &&
          this.admissionForm.admissionReason;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    numberFormat(num) {
      return Number(num || 0).toLocaleString();
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return '-';
      const today = new Date();
      const birth = new Date(dateOfBirth);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    },

    getWardName(wardID) {
      const ward = this.wards.find(w => w.wardID === wardID);
      return ward ? ward.wardName : '';
    },

    getWardRate(wardID) {
      const ward = this.wards.find(w => w.wardID === wardID);
      return ward ? (ward.chargePerDay || ward.dailyRate || 0) : 0;
    },

    getBedNumber(bedID) {
      const bed = this.allBeds.find(b => b.bedID === bedID);
      return bed ? bed.bedNumber : '';
    },

    getWardTypeBadge(type) {
      const badges = {
        'Male': 'bg-primary',
        'Female': 'bg-danger',
        'Pediatric': 'bg-success',
        'Maternity': 'bg-pink',
        'ICU': 'bg-dark',
        'HDU': 'bg-warning text-dark',
        'Newborn': 'bg-info',
        'Isolation': 'bg-secondary',
        'General': 'bg-light text-dark'
      };
      return badges[type] || 'bg-secondary';
    },

    getBedClass(bed) {
      if (this.admissionForm.bedID === bed.bedID) return 'bg-warning text-dark cursor-pointer';
      const status = (bed.bedStatus || bed.status || '').toUpperCase();
      if (status === 'AVAILABLE') return 'bg-success text-white cursor-pointer';
      return 'bg-danger text-white';
    },

    getBedTitle(bed) {
      const status = (bed.bedStatus || bed.status || '').toUpperCase();
      if (status !== 'AVAILABLE') return `Bed ${bed.bedNumber} - Occupied by ${bed.currentPatientName || 'Patient'}`;
      return `Bed ${bed.bedNumber} - Available`;
    },

    selectBed(bed) {
      const status = (bed.bedStatus || bed.status || '').toUpperCase();
      if (status === 'AVAILABLE') {
        this.admissionForm.bedID = bed.bedID;
      }
    },

    // Search Patients
    searchPatients() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      if (this.patientSearch.length < 2) {
        this.patientResults = [];
        return;
      }

      this.searchTimeout = setTimeout(() => {
        const data = new FormData();
        data.append("function", "searchPatient");
        data.append("search", this.patientSearch);

        execute(data, hospital)
            .then((res) => {
              if (res.data.success) {
                this.patientResults = res.data.data.patients || [];
              }
            })
            .catch((e) => console.error(e));
      }, 300);
    },

    selectPatient(patient) {
      this.selectedPatient = {
        patientID: patient.patientID,
        patientNo: patient.patientID,
        fullName: patient.fullNames,
        patientName: patient.fullNames,
        gender: patient.gender,
        age: patient.age,
        dateOfBirth: patient.dateOfBirth,
        phone: patient.phoneNumber,
        phoneNumber: patient.phoneNumber,
        bloodGroup: patient.bloodGroup,
        allergies: patient.allergies
      };
      this.patientSearch = patient.fullNames;
      this.patientResults = [];
    },

    clearPatient() {
      this.selectedPatient = null;
      this.selectedVisit = null;
      this.selectedVisitID = '';
      this.patientSearch = '';
    },

    selectFromQueue() {
      if (!this.selectedVisitID) {
        this.selectedPatient = null;
        this.selectedVisit = null;
        return;
      }
      const visit = this.pendingVisits.find(v => v.visitID === this.selectedVisitID);
      if (visit) {
        this.selectedVisit = visit;
        this.selectedPatient = {
          patientID: visit.patientID,
          patientNo: visit.patientNo || visit.patientID,
          fullName: visit.patientName,
          patientName: visit.patientName,
          gender: visit.gender,
          age: visit.age,
          dateOfBirth: visit.dateOfBirth,
          phone: visit.phone,
          phoneNumber: visit.phone
        };
        this.patientSearch = visit.patientName;
      }
    },

    // Load Data
    loadWards() {
      const data = new FormData();
      data.append("function", "getWardOccupancy");
      data.append("facilityID", this.facilityID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadBeds() {
      this.admissionForm.bedID = '';
      this.beds = [];
      this.allBeds = [];

      if (!this.admissionForm.wardID) {
        this.selectedWard = null;
        return;
      }

      this.selectedWard = this.wards.find(w => w.wardID === this.admissionForm.wardID);

      const data = new FormData();
      data.append("function", "getWardBeds");
      data.append("facilityID", this.facilityID);
      data.append("wardID", this.admissionForm.wardID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.allBeds = res.data.data.beds || [];
              this.beds = this.allBeds.filter(b => b.bedStatus === 'AVAILABLE' || b.status === 'Available');
            }
          })
          .catch((e) => console.error(e));
    },

    loadDoctors() {
      const data = new FormData();
      data.append("function", "getDoctors");
      data.append("facilityID", this.facilityID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.doctors = res.data.data.doctors || res.data.data.staff || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadPendingVisits() {
      const data = new FormData();
      data.append("function", "getPendingAdmissions");
      data.append("facilityID", this.facilityID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.pendingVisits = res.data.data.visits || [];
            }
          })
          .catch((e) => console.error(e));
    },

    setDoctorName() {
      const doc = this.doctors.find(d => d.staffID === this.admissionForm.admittingDoctorID);
      this.admissionForm.admittingDoctorName = doc ? `Dr. ${doc.staffName}` : '';
    },

    // Admit Patient
    admitPatient() {
      if (!this.canAdmit) return;

      this.admitting = true;
      const data = new FormData();
      data.append("function", "admitPatient");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.selectedVisit?.visitID || '');
      data.append("patientID", this.selectedPatient.patientID);
      data.append("patientNo", this.selectedPatient.patientNo || this.selectedPatient.patientID);
      data.append("patientName", this.selectedPatient.fullName || this.selectedPatient.patientName);
      data.append("wardID", this.admissionForm.wardID);
      data.append("bedID", this.admissionForm.bedID);
      data.append("admittingDoctorID", this.admissionForm.admittingDoctorID);
      data.append("admittingDoctorName", this.admissionForm.admittingDoctorName);
      data.append("admissionReason", this.admissionForm.admissionReason);
      data.append("diagnosis", this.admissionForm.diagnosis);
      data.append("admissionType", this.admissionForm.admissionType);
      data.append("expectedLOS", this.admissionForm.expectedLOS);
      data.append("admittedBy", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.admitting = false;
            if (res.data.success) {
              this.admissionResult = res.data.data;
              const modal = new bootstrap.Modal(document.getElementById('successModal'));
              modal.show();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to admit patient');
            }
          })
          .catch((e) => {
            this.admitting = false;
            this.$refs.toast.error('Error admitting patient');
          });
    },

    newAdmission() {
      bootstrap.Modal.getInstance(document.getElementById('successModal')).hide();
      this.clearPatient();
      this.admissionForm = {
        wardID: '',
        bedID: '',
        admittingDoctorID: '',
        admittingDoctorName: '',
        admissionType: 'ELECTIVE',
        admissionReason: '',
        diagnosis: '',
        expectedLOS: ''
      };
      this.beds = [];
      this.allBeds = [];
      this.selectedWard = null;
      this.loadWards();
    }
  },
  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    this.loadWards();
    this.loadDoctors();
    this.loadPendingVisits();
  }
};
</script>

<style scoped>
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
  font-weight: 500;
}

.bg-pink { background-color: #e83e8c; color: #fff; }

.search-results-dropdown {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  position: absolute;
  z-index: 100;
  background: #fff;
  width: calc(100% - 24px);
}

.bed-layout {
  max-height: 200px;
  overflow-y: auto;
}

.bed-item {
  width: 70px;
  cursor: pointer;
  transition: all 0.2s;
}

.bed-item:hover {
  transform: scale(1.05);
}

.cursor-pointer { cursor: pointer; }
</style>